import { useEffect, useRef } from "react";
import { Check, X, ArrowUpRight, Sparkles } from "lucide-react";
import { CONTACT_URL } from "../content";
import { track } from "../lib/analytics";

export default function Plans({ t }) {
  const sectionRef = useRef(null);
  const trackedPricingView = useRef(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || trackedPricingView.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !trackedPricingView.current) {
          trackedPricingView.current = true;
          track("pricing_view");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const handleContactClick = () => track("contact_click");

  return (
    <section
      ref={sectionRef}
      id="plans"
      data-testid="plans-section"
      className="relative py-24 md:py-36 bg-gradient-to-b from-[#030508] via-[#05080f] to-[#030508]"
    >
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <div className="font-mono text-[11px] tracking-[0.3em] text-[var(--primary)] mb-5">
            {t.plans.kicker}
          </div>
          <h2
            data-testid="plans-title"
            className="font-display font-black uppercase leading-[1] text-3xl md:text-5xl mb-6"
          >
            <span className="block text-white">{t.plans.title}</span>
            <span className="block text-[var(--primary)]">
              {t.plans.titleAccent}
            </span>
          </h2>
          <p
            data-testid="plans-subtitle"
            className="text-white/65 text-base md:text-lg max-w-2xl"
          >
            {t.plans.subtitle}
          </p>
        </div>

        {/* Tier grid */}
        <div
          data-testid="plans-grid"
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5"
        >
          {t.plans.tiers.map((tier) => (
            <article
              key={tier.id}
              data-testid={`plan-card-${tier.id}`}
              className={`card-lift relative flex flex-col h-full border p-6 bg-[#0b101e] ${
                tier.popular
                  ? "border-[var(--primary)]/60"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              {/* Crosshair corners */}
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              {tier.popular && (
                <div
                  data-testid={`plan-badge-${tier.id}`}
                  className="absolute -top-3 left-7 px-2 py-1 bg-[var(--primary)] text-black font-mono text-[9px] tracking-[0.3em] font-semibold"
                >
                  {t.plans.mostPopular}
                </div>
              )}

              {/* Code + Name */}
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[10px] tracking-[0.3em] text-white/40">
                  {tier.code}
                </span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    tier.popular ? "bg-[var(--primary)]" : "bg-white/30"
                  } ${tier.popular ? "shadow-[0_0_10px_var(--primary)]" : ""}`}
                />
              </div>

              <h3 className="font-display text-2xl font-bold text-white mb-1">
                {tier.name}
              </h3>
              <p className="text-white/45 text-xs font-mono tracking-wider mb-7">
                {tier.tagline}
              </p>

              {/* Price */}
              <div className="border-y border-white/10 py-5 mb-6">
                <div
                  data-testid={`plan-price-${tier.id}`}
                  className="font-display font-bold text-3xl md:text-4xl text-white"
                >
                  {tier.price}
                </div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2">
                  {tier.period}
                </div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/30 mt-1">
                  {tier.billing}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2.5 mb-5 flex-1">
                {tier.features.map((f, i) => (
                  <li
                    key={i}
                    data-testid={`plan-feature-${tier.id}-${i}`}
                    className="flex items-start gap-3 text-[13px] text-white/75"
                  >
                    <Check
                      className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                        tier.popular ? "text-[var(--primary)]" : "text-white/50"
                      }`}
                      strokeWidth={2}
                    />
                    <span>{f}</span>
                  </li>
                ))}
                {tier.limitations &&
                  tier.limitations.map((l, i) => (
                    <li
                      key={`lim-${i}`}
                      data-testid={`plan-limitation-${tier.id}-${i}`}
                      className="flex items-start gap-3 text-[13px] text-white/45"
                    >
                      <X
                        className="h-4 w-4 mt-0.5 flex-shrink-0 text-white/30"
                        strokeWidth={2}
                      />
                      <span>{l}</span>
                    </li>
                  ))}
              </ul>

              {/* Add-on */}
              {tier.addon && (
                <div
                  data-testid={`plan-addon-${tier.id}`}
                  className="mb-3 border border-dashed border-[var(--primary)]/35 bg-[var(--primary)]/[0.04] px-3 py-2.5 flex items-start gap-2 text-[11px] leading-snug text-white/80"
                >
                  <Sparkles
                    className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[var(--primary)]"
                    strokeWidth={1.8}
                  />
                  <span>{tier.addon}</span>
                </div>
              )}

              {/* Contact note */}
              {tier.contactNote && (
                <p
                  data-testid={`plan-contact-note-${tier.id}`}
                  className="mb-4 font-mono text-[10px] tracking-[0.15em] uppercase text-white/40"
                >
                  {tier.contactNote}
                </p>
              )}

              {/* CTA */}
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${tier.id}`}
                onClick={handleContactClick}
                className={`group inline-flex items-center justify-between gap-3 font-mono text-[11px] tracking-[0.25em] uppercase font-semibold px-5 py-4 transition-all ${
                  tier.popular
                    ? "bg-[var(--primary)] text-black hover:bg-[var(--primary-hover)]"
                    : "border border-white/20 text-white hover:border-[var(--primary)] hover:text-[var(--primary)]"
                }`}
              >
                {tier.ctaOverride || t.plans.contact}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </article>
          ))}
        </div>

        {/* Discount note */}
        <div
          data-testid="plans-discount-note"
          className="mt-12 border border-white/10 bg-[#0b101e]/50 backdrop-blur-sm p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
        >
          <p className="text-white/60 text-sm md:text-base max-w-2xl">
            {t.plans.discountNote}
          </p>
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="plans-discount-cta"
            onClick={handleContactClick}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[var(--primary)] hover:gap-3 transition-all whitespace-nowrap"
          >
            info@scopelink.io <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
